import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Table } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

import {
  ErrorAlert,
  formatDate,
  Loading,
  PageHeader,
  StatusBadge,
} from "../../layout/secure/common";
import {
  currentUsage,
  getResultReport,
  messageFromError,
  runResultAction,
  updateResult,
} from "../../utils/api";
import ExportControls from "../../layout/secure/export-controls";
import { richTextPlainText } from "../../layout/rich-text";

const isOmitted = value =>
  value === undefined
  || value === null
  || (typeof value === "string" && !value.trim())
  || (Array.isArray(value) && !value.length);

const reportQuestionLabel = (value, fallback = "Question") =>
  richTextPlainText(value) || fallback;

function AnswerValue({ value, depth = 0 }) {
  if (isOmitted(value)) return <em className="text-muted">Not answered</em>;
  if (typeof value === "boolean") return <span>{value ? "Yes" : "No"}</span>;
  if (Array.isArray(value)) {
    if (value.every(item => !item || typeof item !== "object")) {
      return (
        <ul className="fera-answer-list">
          {value.map((item, index) => <li key={`${item}-${index}`}>{String(item)}</li>)}
        </ul>
      );
    }
    return (
      <div className="fera-repeating-answers">
        {value.map((item, index) => (
          <section key={index}>
            <h4>Item {index + 1}</h4>
            <AnswerValue value={item} depth={depth + 1} />
          </section>
        ))}
      </div>
    );
  }
  if (typeof value === "object") {
    if (depth > 8) return <span>Nested answer</span>;
    return (
      <dl className="fera-answer-object">
        {Object.entries(value).map(([key, child]) => (
          <div key={key}>
            <dt>{key.replace(/[-_]/g, " ")}</dt>
            <dd><AnswerValue value={child} depth={depth + 1} /></dd>
          </div>
        ))}
      </dl>
    );
  }
  return <span className="fera-answer-text">{String(value)}</span>;
}

function ResponseReport() {
  const { surveyId, responseId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [branding, setBranding] = useState(null);
  const [usage, setUsage] = useState(null);
  const [version, setVersion] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [metadata, setMetadata] = useState({ label: "", notes: "" });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async (selectedVersion = version) => {
    setError("");
    try {
      const [result, usageData] = await Promise.all([
        getResultReport(surveyId, responseId, selectedVersion || null),
        usage ? Promise.resolve(null) : currentUsage(),
      ]);
      setReport(result);
      setVersion(String(result.selectedVersion.version));
      setMetadata({
        label: result.response.metadata?.label || "",
        notes: result.response.metadata?.notes || "",
      });
      if (usageData) {
        setUsage(usageData);
        if (usageData.branding) setBranding(usageData.branding);
      }
    } catch (requestError) {
      setError(messageFromError(requestError, "The response report could not be loaded."));
    }
  }, [responseId, surveyId, usage, version]);

  useEffect(() => {
    let active = true;
    Promise.all([
      getResultReport(surveyId, responseId),
      currentUsage().catch(() => null),
    ])
      .then(([result, usage]) => {
        if (!active) return;
        setReport(result);
        setVersion(String(result.selectedVersion.version));
        setMetadata({
          label: result.response.metadata?.label || "",
          notes: result.response.metadata?.notes || "",
        });
        setBranding(usage?.branding || null);
        setUsage(usage);
      })
      .catch(requestError => {
        if (active) {
          setError(messageFromError(requestError, "The response report could not be loaded."));
        }
      });
    return () => { active = false; };
  }, [responseId, surveyId]);

  const saveMetadata = async event => {
    event.preventDefault();
    setBusy(true);
    try {
      await updateResult(surveyId, responseId, { metadata });
      setMetadataOpen(false);
      toast.success("Response metadata saved.");
      await load(version);
    } catch (requestError) {
      setError(messageFromError(requestError, "Response metadata could not be saved."));
    } finally {
      setBusy(false);
    }
  };

  const toggleSuppressed = async () => {
    setBusy(true);
    try {
      await updateResult(surveyId, responseId, {
        metadata,
        suppressed: !report.response.suppressed,
      });
      toast.success(report.response.suppressed ? "Response restored to reports." : "Response suppressed from aggregate reports.");
      await load(version);
    } catch (requestError) {
      setError(messageFromError(requestError, "The response visibility could not be changed."));
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    setBusy(true);
    try {
      await runResultAction(surveyId, responseId, "reopen");
      toast.success("Response reopened for an eligible identity update.");
      await load(version);
    } catch (requestError) {
      setError(messageFromError(requestError, "The response could not be reopened."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    setBusy(true);
    try {
      await runResultAction(surveyId, responseId, "delete");
      toast.success("Response removed from active reporting.");
      navigate(`/dashboard/surveys/${surveyId}/results`, { replace: true });
    } catch (requestError) {
      setError(messageFromError(requestError, "The response could not be removed."));
      setBusy(false);
    }
  };

  if (!report && !error) return <Loading label="Loading response report" />;
  if (!report) return <ErrorAlert error={error} onRetry={() => load()} />;

  const response = report.response;
  const selectedData = report.data || {};

  return (
    <>
      <div className="fera-print-hidden">
        <PageHeader
          title="Response report"
          description={`${report.survey.name} · ${response.respondent.displayName}`}
          action={(
            <div className="fera-page-buttons">
              <Button as={Link} variant="outline-secondary" to={`/dashboard/surveys/${surveyId}/results`}>Back to results</Button>
              <Button variant="outline-primary" onClick={() => window.print()}>Print</Button>
            </div>
          )}
        />
        <ErrorAlert error={error} onRetry={() => load(version)} />
      </div>

      <article className="fera-report" aria-labelledby="response-report-title">
        <header className="fera-report-header">
          <div>
            {branding?.smallLogo ? <img src={branding.smallLogo} alt="" /> : null}
            <p className="fera-eyebrow">{branding?.name || "FeraSurvey"}</p>
            <h1 id="response-report-title">{report.survey.name}</h1>
            <p>Single-response report</p>
          </div>
          <div className="fera-report-status">
            <StatusBadge status={response.status} />
            <span>Response {response.id.slice(0, 8).toUpperCase()}</span>
          </div>
        </header>

        {response.suppressed ? (
          <Alert variant="warning">This response is suppressed from aggregate reports.</Alert>
        ) : null}

        <section className="fera-report-summary" aria-label="Response summary">
          <dl>
            <div><dt>Respondent</dt><dd>{response.respondent.displayName}</dd></div>
            <div><dt>Verified email</dt><dd>{response.respondent.verifiedEmail || "Not collected"}</dd></div>
            <div><dt>Source</dt><dd>{response.source.label}</dd></div>
            <div><dt>Started</dt><dd>{formatDate(response.startedAt)}</dd></div>
            <div><dt>Last saved</dt><dd>{formatDate(response.lastSavedAt)}</dd></div>
            <div><dt>Submitted</dt><dd>{response.submittedAt ? formatDate(response.submittedAt) : "Not submitted"}</dd></div>
          </dl>
          <Form.Group className="fera-print-hidden" controlId="report-version">
            <Form.Label>Report version</Form.Label>
            <Form.Select
              value={version}
              onChange={event => {
                const next = event.target.value;
                setVersion(next);
                load(next);
              }}
            >
              {report.versions.map(item => (
                <option key={item.id} value={item.version}>
                  v{item.version} · {item.status} · {formatDate(item.savedAt)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <p className="fera-print-only">
            Version {report.selectedVersion.version} ({report.selectedVersion.status})
          </p>
        </section>

        {report.sections.map(section => (
          <section className="fera-report-section" key={section.uuid || section.id || section.key}>
            <h2>{section.caption || section.key}</h2>
            {section.description ? <p>{section.description}</p> : null}
            <dl className="fera-report-answers">
              {(section.Questions || []).filter(question => question.typeName !== "HEADER").map(question => (
                <div key={question.uuid || question.id || question.key}>
                  <dt>
                    {reportQuestionLabel(question.label, question.key)}
                    {question.required ? <span className="visually-hidden"> (required)</span> : null}
                  </dt>
                  <dd>
                    <AnswerValue value={selectedData?.[section.key]?.[question.key]} />
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}

        <section className="fera-report-section">
          <h2>Version and update history</h2>
          <div className="table-responsive">
            <Table className="fera-table" size="sm">
              <thead><tr><th>Version</th><th>Status</th><th>Saved</th><th>Submitted</th></tr></thead>
              <tbody>
                {report.versions.map(item => (
                  <tr key={item.id}>
                    <td>v{item.version}</td>
                    <td>{item.status}</td>
                    <td>{formatDate(item.savedAt)}</td>
                    <td>{item.submittedAt ? formatDate(item.submittedAt) : "\u2014"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {response.reopenedAt ? <p className="text-muted">Reopened {formatDate(response.reopenedAt)}</p> : null}
        </section>

        {response.metadata?.label || response.metadata?.notes ? (
          <section className="fera-report-section">
            <h2>Administrative metadata</h2>
            {response.metadata.label ? <p><strong>Label:</strong> {response.metadata.label}</p> : null}
            {response.metadata.notes ? <p className="fera-pre-wrap">{response.metadata.notes}</p> : null}
          </section>
        ) : null}
      </article>

      <ExportControls
        surveyId={surveyId}
        responseId={responseId}
        usage={usage}
      />

      <section className="fera-panel mt-4 fera-print-hidden">
        <div className="fera-panel-heading">
          <div><p className="fera-eyebrow">Actions</p><h2>Response administration</h2></div>
        </div>
        <div className="fera-row-actions">
          <Button disabled={busy} onClick={() => setMetadataOpen(true)}>Edit metadata</Button>
          {response.status === "submitted" ? (
            <Button variant="outline-primary" disabled={busy} onClick={reopen}>Reopen for editing</Button>
          ) : null}
          <Button variant="outline-secondary" disabled={busy} onClick={toggleSuppressed}>
            {response.suppressed ? "Restore to aggregates" : "Suppress from aggregates"}
          </Button>
          <Button variant="outline-danger" disabled={busy} onClick={() => setConfirmDelete(true)}>Remove response</Button>
        </div>
      </section>

      <Modal show={metadataOpen} onHide={() => !busy && setMetadataOpen(false)} centered>
        <Form onSubmit={saveMetadata}>
          <Modal.Header closeButton><Modal.Title>Response metadata</Modal.Title></Modal.Header>
          <Modal.Body>
            <Form.Group controlId="response-label">
              <Form.Label>Label</Form.Label>
              <Form.Control
                value={metadata.label}
                maxLength={120}
                onChange={event => setMetadata(current => ({ ...current, label: event.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mt-3" controlId="response-notes">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={metadata.notes}
                maxLength={4000}
                onChange={event => setMetadata(current => ({ ...current, notes: event.target.value }))}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="outline-secondary" onClick={() => setMetadataOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? "Saving\u2026" : "Save metadata"}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={confirmDelete} onHide={() => setConfirmDelete(false)} centered>
        <Modal.Header closeButton><Modal.Title>Remove response?</Modal.Title></Modal.Header>
        <Modal.Body>
          This soft-deletes the response from active reports while preserving its version and audit history for retention requirements.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button variant="danger" onClick={remove}>Remove response</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export { AnswerValue, isOmitted, reportQuestionLabel };
export default ResponseReport;
